import { Divider, Stack, Typography } from '@mui/material'

import { LanguageSelectBulkImport } from '../../shared/LanguageSelectBulkImport'
import { PointsBulkImport } from '../../shared/PointsBulkImport'
import { AddTranslationsBulkImport } from '../../shared/AddTranslationsBulkImport'
import { GenerateShortAnswersBulkImport } from '../../shared/GenerateShortAnswersBulkImport'
import {
  GradingMethodMCBulkImport,
  GradingMethodRepeatBulkImport,
  GradingMethodRespondBulkImport,
} from '../../shared/GradingMethodBulkImport'

import { SectionSettingsBulkImport } from './settings/SectionSettingsBulkImport'

import QuestionTypeSelect from '@/views/NewBuilder/components/FormBuilder/QuestionTypeSelect'
import { bulkImportStoreActions } from '@/components/BulkImportModal/store/bulk-import.store'
import { type AllowedCardTypesBulkImport } from '@/components/BulkImportModal/store/bulk-import.store.types'
import { useCardTypeBulkImportStore, useBulkImportStore } from '@/components/BulkImportModal/hooks/useBulkImportStore'
import { checkTypeCardActivity } from '@/utils/checkTypeCard'
import FeedbackLanguageSelector from '@/components/FeedbackSelector/FeedbackLanguageSelector'
import { FeedbackCheckboxes } from '@/components/CardFeedbackTypesCheckboxes'

function DividerSection() {
  return (
    <Divider
      sx={{
        borderColor: '#EAECF0',
      }}
    />
  )
}

export function SettingsCardBulkImport() {
  const settings = useBulkImportStore(store => store.settings)
  const grading = settings.grading

  const cardType = useCardTypeBulkImportStore()

  const { isRepeat, isRespond, isMC, isShortAnswer } = checkTypeCardActivity(cardType)

  const labelLangauge = isRepeat || isMC ? 'Target Language' : 'Language'

  return (
    <Stack
      sx={{
        width: '320px',
        overflowY: 'auto',
        bgcolor: '#fff',
      }}
    >
      <SectionSettingsBulkImport>
        <Typography fontWeight="semibold">Settings</Typography>
      </SectionSettingsBulkImport>
      <DividerSection />
      <SectionSettingsBulkImport className="gap-2">
        <Typography fontWeight="medium">Page Type</Typography>
        <QuestionTypeSelect
          hideMediaArea
          selectedType={cardType}
          onQuestionTypeChange={newCardType => {
            bulkImportStoreActions.updateCardType(newCardType as AllowedCardTypesBulkImport)
          }}
        />
      </SectionSettingsBulkImport>
      <DividerSection />

      <SectionSettingsBulkImport className="gap-2">
        {isRepeat ? (
          <GradingMethodRepeatBulkImport
            leniency={settings.grading.leniency}
            onChange={newLeniency => {
              bulkImportStoreActions.updateGrading({
                leniency: newLeniency,
              })
            }}
          />
        ) : null}

        {isMC ? (
          <GradingMethodMCBulkImport
            type={settings.selectType}
            onChange={newType => {
              bulkImportStoreActions.updateSettings({
                selectType: newType!,
              })
            }}
          />
        ) : null}

        {isRespond ? (
          <GradingMethodRespondBulkImport
            aiFocus={grading.aiFocus}
            generateWithAI={grading.gradingCriteriaWithAI}
            gradingMethod={settings.grading.method}
            performanceExpectations={grading.performanceExpectations}
            points={settings.points}
            rubricId={settings.grading.rubricId}
            studentLevel={grading.studentLevel}
            onChangeAIFocus={newAIFocus => {
              bulkImportStoreActions.updateGrading({
                aiFocus: newAIFocus as 'language' | 'correctness' | 'both',
              })
            }}
            onChangeGradingMethod={newGradingMethod => {
              bulkImportStoreActions.updateGrading({
                method: newGradingMethod as 'simple' | 'rubric' | 'manual',
              })
            }}
            onChangePerformanceExpectations={performanceExpectations => {
              bulkImportStoreActions.updateGrading({
                performanceExpectations,
              })
            }}
            onChangeStudentLevel={studentLevel => {
              bulkImportStoreActions.updateGrading({
                studentLevel,
              })
            }}
            onClickRubric={(rubricId, totalPoints) => {
              bulkImportStoreActions.updateGrading({
                rubricId: rubricId,
              })
              bulkImportStoreActions.updateSettings({
                points: totalPoints,
              })
            }}
            onSwitchGenerateWithAI={generateWithAI => {
              bulkImportStoreActions.updateGrading({
                gradingCriteriaWithAI: generateWithAI,
              })
            }}
          />
        ) : null}
        {isShortAnswer ? (
          <GenerateShortAnswersBulkImport
            checked={settings.generateAnswers}
            handleChange={newGenerateAnswers => {
              bulkImportStoreActions.updateSettings({ generateAnswers: newGenerateAnswers })
            }}
          />
        ) : null}
      </SectionSettingsBulkImport>
      <DividerSection />

      {isRepeat ? (
        <>
          <SectionSettingsBulkImport className="gap-2">
            <AddTranslationsBulkImport
              checked={settings.generateTranslations}
              handleChange={newGenerateTranslations => {
                bulkImportStoreActions.updateSettings({ generateTranslations: newGenerateTranslations })
              }}
            />
          </SectionSettingsBulkImport>
          <DividerSection />
        </>
      ) : null}

      <SectionSettingsBulkImport className="gap-3">
        {isRespond ? (
          <Stack sx={{ gap: '4px' }}>
            <Typography fontWeight="medium">Feedback</Typography>

            <FeedbackCheckboxes
              feedbacks={settings.feedback}
              fontSize="14px"
              handleFeedbacks={newFeedbacks => {
                bulkImportStoreActions.updateFeedback(newFeedbacks)
              }}
              language={settings.language}
            />
          </Stack>
        ) : null}
        <LanguageSelectBulkImport
          handleLanguage={newLanguage => {
            bulkImportStoreActions.updateSettings({ language: newLanguage })
          }}
          label={labelLangauge}
          language={settings.language}
        />
        {isRespond ? (
          <FeedbackLanguageSelector
            selectSx={{
              width: '100%',
            }}
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
            targetLanguage={settings.language}
            value={settings.feedbackLanguage}
            onChange={newFeedbackLanguage => {
              bulkImportStoreActions.updateSettings({ feedbackLanguage: newFeedbackLanguage })
            }}
          />
        ) : null}
      </SectionSettingsBulkImport>

      <DividerSection />
      <SectionSettingsBulkImport className="gap-2">
        <PointsBulkImport
          handleUpdatePointsCard={newPoints => {
            bulkImportStoreActions.updateSettings({ points: newPoints })
          }}
          points={settings.points}
        />
      </SectionSettingsBulkImport>
    </Stack>
  )
}
